import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock3, KeyRound, ShieldAlert, Trash2 } from 'lucide-react';
import { idemApi } from '../lib/api';
import { clearIdemDeviceToken } from '../lib/idemSession';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function StatusBadge({ status }) {
  const tone = {
    active: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    revoked: 'border-red-300 bg-red-50 text-red-700',
    pending: 'border-amber-300 bg-amber-50 text-amber-800',
    approved: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    denied: 'border-red-300 bg-red-50 text-red-700',
    cancelled: 'border-zinc-200 bg-zinc-50 text-zinc-600',
    consumed: 'border-zinc-200 bg-zinc-50 text-zinc-600',
    expired: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-600';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{String(status || 'active').replaceAll('_', ' ')}</span>;
}

function SummaryCard({ title, value, detail }) {
  return (
    <article className="border border-zinc-200 bg-white p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{title}</p>
      <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-zinc-900">{value}</p>
      <p className="mt-2 text-sm leading-6 text-zinc-600">{detail}</p>
    </article>
  );
}

function formatSessionError(value) {
  if (!value) return '';
  if (value === 'digital_key_authenticator_session_not_found') {
    return 'Stored device token could not be verified. Use Forget local device if needed and pair again.';
  }
  return value;
}

export default function IdemSecurity() {
  const [session, setSession] = useState(null);
  const [devices, setDevices] = useState([]);
  const [activationLinks, setActivationLinks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionError, setSessionError] = useState('');
  const [notice, setNotice] = useState('');
  const [activationResult, setActivationResult] = useState(null);
  const [workingDevicePublicId, setWorkingDevicePublicId] = useState('');
  const [generatingActivation, setGeneratingActivation] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await idemApi.get('/idem/security');
      setSession(data.session || null);
      setDevices(data.devices || []);
      setActivationLinks(data.activation_links || []);
      setRequests(data.recent_requests || []);
      setSessionError(data.session_error || '');
    } catch (requestError) {
      setSession(null);
      setDevices([]);
      setActivationLinks([]);
      setRequests([]);
      setSessionError('');
      setError(requestError?.response?.data?.error || 'The lab security view could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeDeviceCount = useMemo(() => devices.filter((device) => device.status === 'active').length, [devices]);
  const revokedDeviceCount = useMemo(() => devices.filter((device) => device.status === 'revoked').length, [devices]);
  const pendingRequestCount = useMemo(() => requests.filter((request) => request.status === 'pending').length, [requests]);
  const activeSession = session?.status === 'active';

  const generateActivationLink = async () => {
    if (!activeSession) return;
    setGeneratingActivation(true);
    try {
      const { data } = await idemApi.post('/idem/security/activation-links', {});
      setActivationResult(data.activation_link || null);
      await load();
      setNotice('A new activation link was generated for this paired account.');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'The activation link could not be generated.');
    } finally {
      setGeneratingActivation(false);
    }
  };

  const revokeDevice = async (devicePublicId) => {
    if (!devicePublicId) return;
    setWorkingDevicePublicId(devicePublicId);
    try {
      const reason = window.prompt('Optional reason for revoking this paired device', 'Device lifecycle review');
      const { data } = await idemApi.post(`/idem/security/devices/${encodeURIComponent(devicePublicId)}/revoke`, reason ? { reason } : {});
      const nextSession = data.session || session;
      setSession(nextSession);
      setDevices((current) => current.map((device) => (
        device.device_public_id === devicePublicId
          ? { ...device, status: 'revoked', revoked_at: data.device?.revoked_at || device.revoked_at || new Date().toISOString() }
          : device
      )));
      if (nextSession?.status === 'revoked' || devicePublicId === session?.device_public_id) {
        clearIdemDeviceToken();
        await load();
        setNotice('This local device has been revoked and the browser token was cleared.');
      } else {
        await load();
        setNotice('The paired device was revoked.');
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'The paired device could not be revoked.');
    } finally {
      setWorkingDevicePublicId('');
    }
  };

  const forgetLocalDevice = () => {
    clearIdemDeviceToken();
    setSession(null);
    setDevices([]);
    setActivationLinks([]);
    setRequests([]);
    setActivationResult(null);
    setSessionError('');
    setError('');
    setNotice('Local device token cleared from this browser.');
  };

  return (
    <main className="min-h-screen bg-zinc-100 px-4 py-6 text-zinc-900 sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-6xl">
        <header className="flex items-center gap-2.5 border-b border-zinc-200 pb-5">
          <div className="grid h-9 w-9 place-items-center bg-black text-sm font-semibold text-white">i</div>
          <div>
            <p className="font-display text-lg font-bold tracking-tight">idem</p>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Device security</span>
          </div>
        </header>

        <section className="mt-5 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="font-medium">Internal lab device lifecycle only</p>
              <p className="mt-1 text-xs leading-5">
                This page is for internal idem pairing and device lifecycle review only. It does not create a public login,
                production OIDC provider, official LoA, CSAM/FAS integration, or regulated identifier flow.
              </p>
            </div>
          </div>
        </section>

        {sessionError && <div className="mt-5 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{formatSessionError(sessionError)}</div>}
        {notice && <div className="mt-5 border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</div>}
        {error && <div className="mt-5 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        {loading ? (
          <div className="mt-6 border border-zinc-200 bg-white p-5 text-sm text-zinc-500">Loading device security…</div>
        ) : !session ? (
          <section className="mt-6 border border-zinc-200 bg-white p-5">
            <p className="font-display text-xl font-semibold tracking-tight text-zinc-900">No paired device</p>
            <p className="mt-2 max-w-3xl text-sm text-zinc-600">
              Open an activation link to pair a device with a digital key account. Once paired, this page will show safe device
              lifecycle state, activation link history, and recent request activity.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/idem/account" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open account</Link>
              <Link to="/idem/requests" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open requests</Link>
              <button type="button" onClick={forgetLocalDevice} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                Forget local device
              </button>
            </div>
          </section>
        ) : (
          <>
            <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <SummaryCard title="Paired devices" value={devices.length} detail="Safe device rows for this account." />
              <SummaryCard title="Active devices" value={activeDeviceCount} detail="Devices still eligible for lab approval." />
              <SummaryCard title="Revoked devices" value={revokedDeviceCount} detail="Devices removed from the paired set." />
              <SummaryCard title="Pending requests" value={pendingRequestCount} detail="Pending approval requests for this account." />
            </section>

            <section className="mt-6 border border-zinc-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Current session</p>
                  <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">{session.status === 'revoked' ? 'Revoked device' : 'Paired lab device'}</h1>
                  <p className="mt-2 max-w-3xl text-sm text-zinc-600">
                    This browser session is tied to the current digital key account. Actions here are limited to the paired account only.
                  </p>
                </div>
                <StatusBadge status={session.status} />
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Device label</p>
                  <p className="mt-1 text-sm text-zinc-700">{session.device_label}</p>
                  <p className="mt-1 font-mono text-xs text-zinc-500">{session.device_public_id}</p>
                </article>
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Account</p>
                  <p className="mt-1 break-all font-mono text-xs text-zinc-700">{session.digital_key_account_id}</p>
                  <p className="mt-1 text-xs text-zinc-500">{session.identity_display_name || session.identity_public_id || '—'}</p>
                </article>
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Last seen</p>
                  <p className="mt-1 text-sm text-zinc-700">{formatDate(session.last_seen_at)}</p>
                </article>
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Created</p>
                  <p className="mt-1 text-sm text-zinc-700">{formatDate(session.created_at || session.device_token_issued_at)}</p>
                </article>
              </div>

              {session.revoked_at && (
                <div className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  This paired device was revoked at {formatDate(session.revoked_at)}.
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/idem/account" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open account</Link>
                <Link to="/idem/requests" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open requests</Link>
                <button type="button" onClick={generateActivationLink} disabled={!activeSession || generatingActivation} className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400 disabled:cursor-not-allowed disabled:opacity-50">
                  Generate new activation link
                </button>
                <button type="button" onClick={forgetLocalDevice} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Forget local device
                </button>
              </div>

              {activationResult?.activation_url && (
                <div className="mt-4 border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                  <p className="font-medium">New activation link generated</p>
                  <p className="mt-1 text-xs text-sky-800">This activation link is shown only once for the lab pairing flow.</p>
                  <p className="mt-2 break-all font-mono text-xs text-sky-900">{activationResult.activation_url}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link to={activationResult.activation_url} className="border border-sky-300 bg-white px-3 py-2 text-xs text-sky-800 hover:border-sky-500">
                      Open activation link
                    </Link>
                    <button
                      type="button"
                      onClick={() => navigator?.clipboard?.writeText(`${window.location.origin}${activationResult.activation_url}`)}
                      className="border border-sky-300 bg-white px-3 py-2 text-xs text-sky-800 hover:border-sky-500"
                    >
                      Copy link
                    </button>
                  </div>
                </div>
              )}
            </section>

            <section className="mt-6 border border-zinc-200 bg-white p-5">
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />
                <h2 className="font-display text-lg font-semibold">Paired devices</h2>
              </div>
              <p className="mt-1 text-sm text-zinc-600">These rows show only safe lifecycle metadata for devices in the current digital key account.</p>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="py-2 pr-3 font-medium">Device</th>
                      <th className="py-2 pr-3 font-medium">Public ID</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 pr-3 font-medium">Last seen</th>
                      <th className="py-2 pr-3 font-medium">Created</th>
                      <th className="py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((device) => {
                      const isCurrent = device.device_public_id === session.device_public_id;
                      return (
                        <tr key={device.id} className="border-b border-zinc-100 last:border-b-0">
                          <td className="py-3 pr-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-zinc-700">{device.device_label}</span>
                              {isCurrent && <span className="border border-sky-200 bg-sky-50 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-sky-700">Current</span>}
                            </div>
                          </td>
                          <td className="py-3 pr-3 font-mono text-xs text-zinc-700">{device.device_public_id}</td>
                          <td className="py-3 pr-3"><StatusBadge status={device.status} /></td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{formatDate(device.last_seen_at)}</td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{formatDate(device.created_at)}</td>
                          <td className="py-3">
                            <button
                              type="button"
                              onClick={() => revokeDevice(device.device_public_id)}
                              disabled={!activeSession || workingDevicePublicId === device.device_public_id || device.status === 'revoked'}
                              className="inline-flex items-center gap-1.5 border border-red-200 px-3 py-2 text-xs text-red-700 hover:border-red-400 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                              Revoke
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-6 grid gap-6 lg:grid-cols-2">
              <div className="border border-zinc-200 bg-white p-5">
                <div className="flex items-center gap-2">
                  <Clock3 className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />
                  <h2 className="font-display text-lg font-semibold">Activation link history</h2>
                </div>
                <p className="mt-1 text-sm text-zinc-600">Activation links are stored safely. The raw activation token is only returned once at creation time.</p>
                {activationLinks.length ? (
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                        <tr>
                          <th className="py-2 pr-3 font-medium">Link</th>
                          <th className="py-2 pr-3 font-medium">Status</th>
                          <th className="py-2 pr-3 font-medium">Issued</th>
                          <th className="py-2 pr-3 font-medium">Expires</th>
                          <th className="py-2 font-medium">Consumed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activationLinks.map((link) => (
                          <tr key={link.id} className="border-b border-zinc-100 last:border-b-0">
                            <td className="py-3 pr-3 font-mono text-xs text-zinc-700">{link.id}</td>
                            <td className="py-3 pr-3"><StatusBadge status={link.status} /></td>
                            <td className="py-3 pr-3 text-xs text-zinc-600">{formatDate(link.created_at || link.activation_token_issued_at)}</td>
                            <td className="py-3 pr-3 text-xs text-zinc-600">{formatDate(link.activation_token_expires_at)}</td>
                            <td className="py-3 text-xs text-zinc-600">{link.activation_consumed_at ? `${formatDate(link.activation_consumed_at)}${link.activation_consumed_device_id ? ` · ${link.activation_consumed_device_id}` : ''}` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mt-4 border border-dashed border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-600">
                    No activation links yet for this account.
                  </div>
                )}
              </div>

              <div className="border border-zinc-200 bg-white p-5">
                <div className="flex items-center gap-2">
                  <Clock3 className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />
                  <h2 className="font-display text-lg font-semibold">Recent request activity</h2>
                </div>
                <p className="mt-1 text-sm text-zinc-600">A compact safe trail of recent login requests for this paired account.</p>
                {requests.length ? (
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                        <tr>
                          <th className="py-2 pr-3 font-medium">Request</th>
                          <th className="py-2 pr-3 font-medium">Status</th>
                          <th className="py-2 pr-3 font-medium">Assurance</th>
                          <th className="py-2 font-medium">Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map((request) => (
                          <tr key={request.id} className="border-b border-zinc-100 last:border-b-0">
                            <td className="py-3 pr-3 font-mono text-xs text-zinc-700">{request.id}</td>
                            <td className="py-3 pr-3"><StatusBadge status={request.status} /></td>
                            <td className="py-3 pr-3 text-xs text-zinc-600">{String(request.requested_assurance || 'internal_foundation').replaceAll('_', ' ')}</td>
                            <td className="py-3 text-xs text-zinc-600">{formatDate(request.updated_at || request.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mt-4 border border-dashed border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-600">
                    No recent request activity for this paired account.
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
